import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { getListingImageAltText } from '../../../util/listingImageAlt';
import * as log from '../../../util/log';
import { AspectRatioWrapper, IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import ProgressiveResponsiveImage from './ProgressiveResponsiveImage';
import { openPhotoViewer, toViewerSlides } from './photoViewer';
import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const IMAGE_SIZES = '(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px';
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

// Fallback variants tried when the requested scaled-* variant isn't
// on the entity yet (typical when the SearchPage cached the listing
// at listing-card size and showListing hasn't returned the scaled
// variants). Same aspect ratio as the scaled variants, so the
// container shape is right on first paint.
const ASPECT_RATIO_FALLBACK_VARIANTS = [
  'scaled-medium',
  'scaled-large',
  'scaled-xlarge',
  'listing-card-2x',
  'listing-card',
];

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const variants = firstImage?.attributes?.variants || {};
  // Prefer the requested variant; fall back through the priority list
  // so we still get a portrait container before showListing resolves.
  // Without this the no-variant case defaults to 1:1, which
  // square-shifts on first paint and lays out the carousel chrome
  // around the wrong aspect ratio.
  const v =
    variants[scaledVariant] ||
    ASPECT_RATIO_FALLBACK_VARIANTS.map(name => variants[name]).find(Boolean);
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : // No variants of any size — default to 4:3 portrait, the typical
      // Zuprize fashion-photo shape, so the placeholder reads correctly
      // before any image data arrives.
      { aspectWidth: 4, aspectHeight: 3 };
};

// listing.images and the variant arrays are new objects on every store
// update (a heart tap, the share dialog), and react-image-gallery goes back to
// the first photo whenever its items change. Keep the same items while what
// they show is the same.
const useStableItems = items => {
  const key = items
    .map(
      item =>
        `${item.image.id?.uuid}|${Object.keys(item.image.attributes?.variants || {}).join(',')}|${
          item.thumbnail?.name
        }|${item.alt}|${item.thumbAlt}`
    )
    .join('\n');
  const ref = useRef({ key: null, items: null });
  if (ref.current.key !== key) {
    ref.current = { key, items };
  }
  return ref.current.items;
};

/**
 * The ListingImageGallery component.
 *
 * Phones (below --viewportMedium): each photo whole in a 3:4 frame, swipe
 * between photos, a "1/5" counter, no thumbnails and no arrows. From
 * --viewportMedium: thumbnails and arrows. On every screen a tap or click on
 * the photo, or "Vis store bilder" on desktop, opens the full-screen viewer
 * (PhotoSwipe, loaded on that first tap; see photoViewer.js).
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {Object} [props.listing] - The listing; when given, images get descriptive
 *   alt text (title – colour, size – what the photo shows) instead of "annonsefoto n/N"
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [slide, setSlide] = useState({ items: null, index: 0 });
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const galleryRef = useRef(null);
  // Set from the tap until the viewer closes, so a second tap while
  // PhotoSwipe loads doesn't open a second viewer.
  const isViewerOpenRef = useRef(false);
  const intl = useIntl();
  const config = useConfiguration();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, listing } = props;
  const listingFields = config?.listing?.listingFields;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = useStableItems(
    images.map((img, i) => {
      // Descriptive alt (SEO + screen readers) when the listing is known;
      // the generic "annonsefoto n/N" only as a last resort.
      const descriptiveAlt = listing
        ? getListingImageAltText({
            listing,
            image: img,
            index: i + 1,
            total: images.length,
            listingFields,
            intl,
          })
        : null;
      return {
        // We will only use the image resource, but react-image-gallery
        // requires the `original` key from each item.
        original: '',
        alt:
          descriptiveAlt ||
          intl.formatMessage(
            { id: 'ListingImageGallery.imageAltText' },
            { index: i + 1, count: images.length }
          ),
        thumbAlt:
          descriptiveAlt ||
          intl.formatMessage(
            { id: 'ListingImageGallery.imageThumbnailAltText' },
            { index: i + 1, count: images.length }
          ),
        thumbnail: img.attributes?.variants?.[thumbVariants[0]],
        image: img,
      };
    })
  );
  // The gallery starts again at the first photo when its items change.
  const currentIndex = slide.items === items ? slide.index : 0;
  const galleryIndex = () => galleryRef.current?.getCurrentIndex() ?? currentIndex;

  // index: the photo to open; the gallery's current photo when not given.
  const openViewer = index => {
    if (isViewerOpenRef.current) {
      return;
    }
    isViewerOpenRef.current = true;
    openPhotoViewer({
      slides: toViewerSlides(images, items.map(item => item.alt)),
      index: index ?? galleryIndex(),
      closeTitle: intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' }),
      prevTitle: intl.formatMessage({ id: 'ListingImageGallery.previousPhoto' }),
      nextTitle: intl.formatMessage({ id: 'ListingImageGallery.nextPhoto' }),
      errorMsg: intl.formatMessage({ id: 'ResponsiveImage.noImage' }),
      onClose: closedAtIndex => {
        isViewerOpenRef.current = false;
        setIsViewerOpen(false);
        // Back in the gallery on the photo the visitor last looked at.
        if (galleryRef.current && closedAtIndex !== galleryIndex()) {
          galleryRef.current.slideToIndex(closedAtIndex);
        }
      },
    })
      .then(pswp => {
        if (pswp) {
          setIsViewerOpen(true);
        } else {
          // The visitor left the page while PhotoSwipe loaded.
          isViewerOpenRef.current = false;
        }
      })
      .catch(e => {
        // E.g. the PhotoSwipe chunk failed to download: let the next tap retry.
        isViewerOpenRef.current = false;
        log.error(e, 'listing-photo-viewer-failed');
      });
  };

  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ProgressiveResponsiveImage
            className={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            sizes={IMAGE_SIZES}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        {/* Lazy: thumbnails are hidden on phones, where they then never load. */}
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
          loading="lazy"
        />
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  // react-image-gallery's own fullscreen is not used: its slot holds the
  // desktop "Vis store bilder" button, which opens the viewer.
  const renderFullscreenButton = () => {
    return (
      <button type="button" className={css.openFullscreen} onClick={() => openViewer()}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };
  // Phones: "1/5" on the photo. One photo needs no counter.
  const renderCustomControls = () => {
    return items.length > 1 ? (
      <span className={css.counter}>
        <FormattedMessage
          id="ListingImageGallery.imageCounter"
          values={{ index: currentIndex + 1, count: items.length }}
        />
      </span>
    ) : null;
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onBeforeSlide={index => setSlide({ items, index })}
      onClick={() => openViewer()}
      // The arrow keys move the viewer, not the gallery behind it.
      disableKeyDown={isViewerOpen}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      renderCustomControls={renderCustomControls}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
